'use strict';

export class WidgetManager {
	static $inject = [
		'$q',
		'mx.shell.UserProfileService',
		'mx.SolutionBuilderAgent.ContentWidgetService',
		'mx.shell.DetailsView',
		'mx.SolutionBuilderAgent.PersonalizationService',
		'mx.shell.Config',
		'mx.sb.DialogView.DialogFactory',
		'mx.SolutionBuilderAgent.EntityService',
	];

	constructor($q, userProfileService, contentWidgetService, DetailsView, personalizationService, mxConfig, dialogFactory, entityService) {
		Object.assign(this, {
			$q,
			userProfileService,
			contentWidgetService,
			DetailsView,
			personalizationService,
			mxConfig,
			dialogFactory,
			entityService,
		});

		this.viewTypes = {};
		this._openDialogIfNoPreview = mxConfig.get('openDialogIfNoPreview') || false;
	}

	filterViews(views, dialogActionType) {
		const defaultDialogFilter = function (view) {
			return (
				view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.Dialog &&
				((dialogActionType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.New && view.Purpose !== mx.SolutionBuilderAgent.ContentWidgetDialogPurpose.EditOnly) ||
					(dialogActionType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit && view.Purpose !== mx.SolutionBuilderAgent.ContentWidgetDialogPurpose.CreateOnly))
			);
		};

		return views.filter(dialogActionType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Preview ? this.isPreview : defaultDialogFilter);
	}

	async getPreviewView(objectDetails, widgetId, extraLocals = {}, presetFields = {}) {
		if (!objectDetails) {
			return;
		}

		objectDetails = angular.copy(objectDetails);
		objectDetails.EntityType =
			objectDetails['Sys-EntityType'] || objectDetails.EntityType || objectDetails['Sys-Entity'] || objectDetails.ConfigurationItemTypeName || objectDetails._type;
		objectDetails.Id =
			objectDetails['Sys-ObjectId2'] ||
			objectDetails._id ||
			objectDetails['Sys-ObjectId'] ||
			objectDetails.ObjectId ||
			objectDetails.objectId ||
			objectDetails.Id ||
			objectDetails.ID;
		objectDetails.IsArchived = objectDetails._archived || objectDetails.IsArchived;

		if (!objectDetails.EntityType) {
			return;
		}

		const context = {
			_id: objectDetails.Id,
			_type: objectDetails.EntityType,
			_name: objectDetails._name || objectDetails.DisplayString,
			_state: objectDetails._state || objectDetails['Sys-EntityState'],
			_displayName: objectDetails._displayName || objectDetails.DisplayString,
			_archived: objectDetails.IsArchived,
		};

		await this.entityService.processObjectContext(context);

		const { dialogInsteadOfPreview, views } = await this.getViewsForPreview(context._type, Boolean(objectDetails.print));

		const selectedWidget = widgetId && views.find((view) => view.Id === widgetId);
		let personalizedWidget;

		if (!selectedWidget) {
			const personalizedWidgetID = await this.personalizationService.getDefaultPreviewId(context._type);
			personalizedWidget = personalizedWidgetID && views.find((view) => view.Id === personalizedWidgetID);
		}

		let view = selectedWidget || personalizedWidget || views[0];

		if (!view) {
			const isAdmin = await this.userProfileService.isAdminMode();
			if (!isAdmin) {
				return null;
			}
			view = {};
		} else {
			view.EntityDisplayName = objectDetails._getTypeDisplayName ? await objectDetails._getTypeDisplayName : view.EntityDisplayName || ''; //TODO: check for DD and CI
		}

		const detailsView = this.dialogFactory.createPreview(context, view, {
			//TODO: remove it from extraLocals
			hideActions: Boolean(objectDetails.hideActions || extraLocals.hideActions),
			isSingleView: Boolean(objectDetails.isSingleView || extraLocals.isSingleView),
			readOnly: Boolean(objectDetails.readOnly || extraLocals.readOnly),
			dialogInsteadOfPreview: dialogInsteadOfPreview,
			presetFields: presetFields,
		});

		Object.assign(detailsView.locals, extraLocals);

		return detailsView;
	}

	getViewsForPreview(type, forAllApps) {
		var dialogInsteadOfPreview = false;

		// In case of isPrint === true we must ignore current application

		return this.getPreviews(type, forAllApps)
			.then((views) => {
				if (!views.length && this._openDialogIfNoPreview) {
					dialogInsteadOfPreview = true;
					return this.getEditDialogs(type, undefined, forAllApps);
				}
				return views;
			})
			.then((views) => {
				return {
					views: views,
					dialogInsteadOfPreview: dialogInsteadOfPreview,
				};
			});
	}

	isPreview(view) {
		return view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.ObjectDetails;
	}

	isEditDialog(view) {
		return (
			view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.Dialog &&
			(typeof view.Purpose === 'undefined' ||
				view.Purpose === mx.SolutionBuilderAgent.ContentWidgetDialogPurpose.EditOnly ||
				view.Purpose === mx.SolutionBuilderAgent.ContentWidgetDialogPurpose.CreateAndEdit)
		);
	}

	isAdHocDialog(view) {
		return view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.Dialog && Boolean(view.NotUseInCreateEditPurposes);
	}

	getSortedViews(objectTypeName, forAllApps) {
		return this.contentWidgetService
			.getViews(objectTypeName, forAllApps)
			.then((views) => {
				return views.sort((view1, view2) => {
					return (view2.Priority || 0) - (view1.Priority || 0);
				});
			})
			.catch(() => {
				return [];
			});
	}

	isNewDialog(view) {
		return (
			view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.Dialog &&
			(typeof view.Purpose === 'undefined' ||
				view.Purpose === mx.SolutionBuilderAgent.ContentWidgetDialogPurpose.CreateOnly ||
				view.Purpose === mx.SolutionBuilderAgent.ContentWidgetDialogPurpose.CreateAndEdit)
		);
	}

	getPreviews(objectTypeName, forAllApps) {
		return this.getSortedViews(objectTypeName, forAllApps).then((views) => views.filter((view) => this.isPreview(view)));
	}

	getNewDialogs(objectTypeName, viewId) {
		return this.getSortedViews(objectTypeName).then((views) => {
			return views.filter((view) => this.isNewDialog(view) || (viewId && this.isAdHocDialog(view)));
		});
	}

	getEditDialogs(objectTypeName, viewId, forAllApps) {
		return this.getSortedViews(objectTypeName, forAllApps).then((views) => {
			return views.filter((view) => this.isEditDialog(view) || (viewId && this.isAdHocDialog(view)));
		});
	}

	getPreviewsOrDialogs(objectTypeName) {
		return this.getSortedViews(objectTypeName).then((views) => {
			var previews = views.filter((view) => this.isPreview(view));
			if (previews.length) {
				return previews;
			}
			return views.filter((view) => this.isNewDialog(view));
		});
	}

	async hasNewDialogs(objectTypeName) {
		return (await this.getNewDialogs(objectTypeName)).length > 0;
	}

	async hasEditDialogs(objectTypeName) {
		return (await this.getEditDialogs(objectTypeName)).length > 0;
	}

	async hasPreviews(objectTypeName) {
		return (await this.getPreviews(objectTypeName)).length > 0;
	}

	async hasPreviewsOrDialogs(objectTypeName) {
		return (await this.getPreviewsOrDialogs(objectTypeName)).length > 0;
	}

	async getAlternativeViews(view = {}) {
		if (!view.context || !view.context._type) {
			return [];
		}
		switch (view.dialogType) {
			case mx.SolutionBuilderAgent.ContentWidgetDialogAction.Preview:
				return (await this.getViewsForPreview(view.context._type)).views;
			case mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit:
				return this.getEditDialogs(view.context._type);
			case mx.SolutionBuilderAgent.ContentWidgetDialogAction.New:
				return this.getNewDialogs(view.context._type);
			default:
				return [];
		}
	}
}
